import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Grid, 
  Card, 
  CardContent, 
  CardActions,
  Button,
  Divider,
  Fab
} from '@mui/material';
import { 
  Notifications as NotificationsIcon,
  Settings as SettingsIcon,
  ArrowForward as ArrowForwardIcon,
  Feedback as FeedbackIcon,
  Dashboard as DashboardIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { RoutesEnum } from '../../../../utils/routes';
import MainLayout from '../../../../components/Layout/MainLayout';
import NPSSurveyModal from '../../../../components/Survey/NPSSurveyModal';

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [surveyModalOpen, setSurveyModalOpen] = useState(false);

  // Randomly show NPS survey on dashboard mount (1 in 20 chance).
  // Modo aleatório de 1 a 20, em que se for "1", deve mostrar a modal de Survey.
  useEffect(() => {
    const randomValue = Math.floor(Math.random() * 20) + 1; // 1..20
    if (randomValue === 1) {
      setSurveyModalOpen(true);
    }
  }, []);

  const handleOpenSurvey = () => {
    setSurveyModalOpen(true);
  };

  const handleCloseSurvey = () => {
    setSurveyModalOpen(false);
  };

  const cards = [
    {
      title: t('dashboard_card_features_title'),
      description: t('dashboard_card_features_description'),
      icon: <SettingsIcon fontSize="large" color="primary" />,
      action: () => navigate(t(RoutesEnum.FEATURES.HREF(t))),
    },
    {
      title: t('dashboard_card_notifications_title'),
      description: t('dashboard_card_notifications_description'),
      icon: <NotificationsIcon fontSize="large" color="primary" />,
      action: () => navigate(t(RoutesEnum.NOTIFICATIONS.HREF(t))),
    },
  ];

  return (
    <MainLayout>
      <Box sx={{ flexGrow: 1 }}>
        <Typography variant="h4" component="h1" gutterBottom color="primary" sx={{ fontWeight: 'bold', mb: 4, display: 'flex', alignItems: 'center', gap: 1 }}>
          <DashboardIcon />
          {t('dashboard_title_welcome')}
        </Typography>
        
        <Typography variant="body1" paragraph>
          {t('dashboard_description_main_1')}
          {' '}
          {t('dashboard_description_main_2')}
        </Typography>
        
        <Divider sx={{ my: 4 }} />
        
        <Grid container spacing={4}>
          {cards.map((card, index) => (
            <Grid item component={'div' as React.ElementType} key={index} xs={12} sm={6} md={6}>
              <Card 
                sx={{ 
                  height: '100%', 
                  display: 'flex', 
                  flexDirection: 'column',
                  transition: 'transform 0.2s',
                  '&:hover': {
                    transform: 'translateY(-5px)',
                    boxShadow: 4
                  }
                }}
              >
                <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', p: 3 }}>
                  <Box sx={{ mb: 2 }}>
                    {card.icon}
                  </Box>
                  <Typography gutterBottom variant="h5" component="h2" align="center">
                    {card.title}
                  </Typography>
                  <Typography align="center">
                    {card.description}
                  </Typography>
                </CardContent>
                <CardActions>
                  <Button 
                    size="small" 
                    onClick={card.action}
                    endIcon={<ArrowForwardIcon />}
                    sx={{ ml: 'auto', mr: 'auto', mb: 2 }}
                  >
                    {t('dashboard_card_btn_access')}
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Floating Action Button for Survey */}
      <Fab
        color="primary"
        aria-label={t('dashboard_fab_feedback_aria')}
        onClick={handleOpenSurvey}
        sx={{
          position: 'fixed',
          bottom: 16,
          right: 16,
          zIndex: 1000
        }}
      >
        <FeedbackIcon />
      </Fab>

      {/* NPS Survey Modal */}
      <NPSSurveyModal
        open={surveyModalOpen}
        onClose={handleCloseSurvey}
      />
    </MainLayout>
  );
};

export default Dashboard;
